import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ImagePlus, Trash2, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  fetchProduct,
  adminUpdateProduct,
  adminUploadProductImage,
  adminRemoveProductImage,
  resolveImageUrl,
} from "../lib/api";

const FIELDS = [
  ["sku", "SKU", "text"],
  ["name", "Name", "text"],
  ["dosage", "Dosage", "text"],
  ["price_gbp", "Price (GBP)", "number"],
  ["stock", "Stock", "number"],
  ["category", "Category", "text"],
  ["cas_number", "CAS number", "text"],
  ["molecular_weight", "Molecular weight", "text"],
  ["purity", "Purity", "text"],
];

const AdminProductEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [drag, setDrag] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    fetchProduct(id)
      .then((p) => {
        setProduct(p);
        setForm({ ...p });
      })
      .catch(() => toast.error("Could not load product."));
  }, [id]);

  const handleFile = useCallback(
    async (file) => {
      if (!file) return;
      if (!/^image\/(jpeg|jpg|png|webp|gif)$/.test(file.type)) {
        toast.error("Use JPG, PNG, WebP or GIF.");
        return;
      }
      if (file.size > 6 * 1024 * 1024) {
        toast.error("Image too large (max 6MB).");
        return;
      }
      setUploading(true);
      setProgress(0);
      try {
        const updated = await adminUploadProductImage(id, file, setProgress);
        setProduct(updated);
        setForm((f) => ({ ...f, image_url: updated.image_url }));
        toast.success("Image uploaded");
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Upload failed");
      } finally {
        setUploading(false);
        setProgress(0);
      }
    },
    [id],
  );

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  };

  const onPick = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = "";
  };

  const removeImage = async () => {
    if (!window.confirm("Remove this image?")) return;
    try {
      const updated = await adminRemoveProductImage(id);
      setProduct(updated);
      setForm((f) => ({ ...f, image_url: null }));
      toast.success("Image removed");
    } catch (e) {
      toast.error("Remove failed");
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form) return;
    setSaving(true);
    const patch = {
      sku: form.sku,
      name: form.name,
      dosage: form.dosage,
      price_gbp: Number(form.price_gbp) || 0,
      stock: Number(form.stock) || 0,
      category: form.category,
      cas_number: form.cas_number || null,
      molecular_weight: form.molecular_weight || null,
      purity: form.purity,
      short_description: form.short_description || "",
      description: form.description || "",
    };
    try {
      const updated = await adminUpdateProduct(id, patch);
      setProduct(updated);
      setForm({ ...updated });
      toast.success("Saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!product || !form)
    return <div className="text-center text-[#64748B] py-16">Loading…</div>;

  const imgPreview = resolveImageUrl(form.image_url);

  return (
    <div data-testid="admin-product-edit-page">
      <Link
        to="/admin/products"
        className="inline-flex items-center gap-2 text-sm text-[#475569] sb-link-underline mb-6"
      >
        <ArrowLeft className="w-4 h-4" /> Back to all products
      </Link>

      <div className="grid lg:grid-cols-5 gap-8">
        {/* Image upload */}
        <div className="lg:col-span-2">
          <div className="sb-mono-label mb-3">Product image</div>
          <div
            data-testid="image-dropzone"
            onDragOver={(e) => {
              e.preventDefault();
              setDrag(true);
            }}
            onDragLeave={() => setDrag(false)}
            onDrop={onDrop}
            onClick={() => fileRef.current?.click()}
            className={`relative aspect-square border-2 border-dashed transition-colors cursor-pointer ${
              drag
                ? "border-[#2C8C9C] bg-[#F0FDFE]"
                : "border-[#CBD5E1] bg-[#F8FAFC] hover:border-[#2C8C9C]"
            } flex items-center justify-center text-center p-6 overflow-hidden`}
          >
            {imgPreview ? (
              <>
                <img src={imgPreview} alt="" className="w-full h-full object-cover" />
                {uploading && (
                  <div className="absolute inset-0 bg-white/80 flex flex-col items-center justify-center gap-2">
                    <Loader2 className="w-6 h-6 text-[#1B3A5C] animate-spin" />
                    <div className="font-mono text-xs text-[#1B3A5C]">{progress}%</div>
                  </div>
                )}
              </>
            ) : uploading ? (
              <div className="flex flex-col items-center gap-3 text-[#1B3A5C]">
                <Loader2 className="w-7 h-7 animate-spin" />
                <div className="font-mono text-xs">{progress}% uploaded</div>
              </div>
            ) : (
              <div className="text-[#64748B]">
                <ImagePlus className="w-10 h-10 mx-auto text-[#2C8C9C]/60" strokeWidth={1.25} />
                <div className="mt-3 text-sm font-medium text-[#1B3A5C]">Drag &amp; drop image</div>
                <div className="mt-1 text-xs">or click to browse</div>
                <div className="mt-3 text-[10px] font-mono uppercase tracking-wider">
                  JPG · PNG · WebP · max 6MB
                </div>
              </div>
            )}
            <input
              ref={fileRef}
              data-testid="image-file-input"
              type="file"
              accept="image/*"
              hidden
              onChange={onPick}
            />
          </div>
          {imgPreview && (
            <button
              type="button"
              data-testid="image-remove"
              onClick={removeImage}
              className="mt-3 inline-flex items-center gap-1.5 border border-[#B91C1C] text-[#B91C1C] hover:bg-[#B91C1C] hover:text-white px-3 py-1.5 text-xs font-medium tracking-wide transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" /> Remove image
            </button>
          )}
        </div>

        {/* Fields */}
        <form onSubmit={save} className="lg:col-span-3 space-y-6">
          <div className="grid sm:grid-cols-2 gap-4">
            {FIELDS.map(([key, label, type]) => (
              <label key={key} className="block">
                <span className="sb-mono-label">{label}</span>
                <input
                  data-testid={`field-${key}`}
                  type={type}
                  step={type === "number" ? "any" : undefined}
                  value={form[key] ?? ""}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  className="mt-2 block w-full border border-[#E2E8F0] focus:border-[#2C8C9C] focus:outline-none px-3 py-2.5 text-sm bg-white"
                />
              </label>
            ))}
          </div>

          <label className="block">
            <span className="sb-mono-label">Short description</span>
            <input
              data-testid="field-short_description"
              type="text"
              value={form.short_description ?? ""}
              onChange={(e) => setForm({ ...form, short_description: e.target.value })}
              className="mt-2 block w-full border border-[#E2E8F0] focus:border-[#2C8C9C] focus:outline-none px-3 py-2.5 text-sm bg-white"
            />
          </label>

          <label className="block">
            <span className="sb-mono-label">Description</span>
            <textarea
              data-testid="field-description"
              rows={5}
              value={form.description ?? ""}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="mt-2 block w-full border border-[#E2E8F0] focus:border-[#2C8C9C] focus:outline-none px-3 py-2.5 text-sm bg-white"
            />
          </label>

          <div className="flex items-center justify-between gap-3 pt-4 border-t border-[#E2E8F0]">
            <button
              type="button"
              onClick={() => navigate("/admin/products")}
              className="text-sm text-[#64748B] hover:text-[#1B3A5C]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="product-save"
              className="bg-[#1B3A5C] text-white hover:bg-[#254D7A] px-7 py-3 text-sm font-medium tracking-wide transition-colors disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminProductEdit;
